import unauthAxios, { unauthAxiosFetcher } from "../axios";
import authAxios, { authAxiosFetcher } from "../authAxios";
import useSWR, { mutate } from "swr";
import { objectToQueryParams } from "../utils/utils";
import i18n from "../i18n";

// Single source of truth for the ongoing-promotions payload (welcome deals, flash
// deals, code eligibility, and the duck-fam leader discount). The backend
// (getSpecialDiscountedTrips) computes the duck-fam %/name from duck_code +
// duck_redirect_trip — callers must NEVER hardcode the discount; they pass the
// duck params and render whatever the backend returns.
// When signed in, the JWT is sent too so the backend can include the user's
// saved duck-fam invites (they're private, so a bare user_id doesn't get them).
// Sent as a plain header on the unauthenticated client: a stale token must not
// trigger authAxios's sign-out redirect on this background call.
export const getOngoingPromotions = ({
  userId = "",
  duckCode = null,
  duckRedirectTrip = null,
  signal,
} = {}) => {
  const token = userId ? localStorage.getItem("JWT_TOKEN") : null;
  return unauthAxios
    .get(`/v1/trip/getOngoingPromotionsForTrips`, {
      params: {
        user_id: userId ?? "",
        duck_code: duckCode,
        duck_redirect_trip: duckRedirectTrip,
      },
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      signal,
    })
    .then((res) => res.data);
};

export const useTrips = () => useSWR(`/v1/trip/getTrips`, unauthAxiosFetcher);

export const useUserInterestedAndDiscountedTrips = () => {
  const userId = localStorage.getItem("USER_ID");
  return useSWR(
    userId ? `/v1/trip/getUserInterestedAndDiscountedTrips?user_id=${userId}` : null,
    unauthAxiosFetcher
  );
};

export const useActiveTripForUser = (userId) => {
  return useSWR(
    userId ? `/v1/trip/getActiveTripForUser?user_id=${userId}` : null,
    unauthAxiosFetcher
  );
};

export const useTrendingTrips = () => useSWR(`/v1/trip/getTrendingTrips`, unauthAxiosFetcher);
// Weekly deal rows for the home page: { welcomeDeals, lastMinuteDeals }.
// Welcome offers show to new users, last-minute deals to existing users.
export const useWeeklyDeals = () => useSWR(`/v1/trip/getWeeklyDeals`, unauthAxiosFetcher);
// Token-gated preview of an unpublished weekly-deals batch (portal "Preview
// on site" link) — same shape as useWeeklyDeals plus a `preview` meta object.
export const useWeeklyDealsPreview = (batchId, token) =>
  useSWR(
    batchId && token
      ? `/v1/trip/getWeeklyDealsPreview?batch_id=${batchId}&token=${token}`
      : null,
    unauthAxiosFetcher
  );
export const useSofestTobyatoTrips = () =>
  useSWR(`/v1/trip/getSofestTobyatoTrips`, unauthAxiosFetcher);

const tripKey = (tripId, getFromCache, edit_trip) =>
  tripId
    ? [`/v1/trip/getTrip?trip_id=${tripId}&getFromCache=${getFromCache}&edit_trip=${edit_trip}`, i18n.language]
    : null;

export const useTrip = (tripId, getFromCache = false, edit_trip = false) =>
  useSWR(tripKey(tripId, getFromCache, edit_trip), ([url]) => unauthAxiosFetcher(url));

// Loads a trip into the same cache entry useTrip(tripId, getFromCache) reads,
// so a page navigated to afterwards renders without a loading screen.
export const prefetchTrip = (tripId, getFromCache = false) => {
  const key = tripKey(tripId, getFromCache, false);
  return mutate(key, unauthAxiosFetcher(key[0]), { revalidate: false });
};

// `access`: the private-trip code from the page URL. Public trip payloads
// only include access_code when the request already carries the right code.
export const getTrip = (tripId, getFromCache = false, edit_trip = false, access = null) =>
  unauthAxios.get("/v1/trip/getTrip", {
    params: {
      trip_id: tripId,
      getFromCache: getFromCache,
      edit_trip: edit_trip,
      ...(access ? { access } : {}),
    },
  });

// Private trips: the typed code is checked on the server → { valid }.
export const checkTripAccessCode = (tripId, code) =>
  unauthAxios.post("/v1/trip/checkAccessCode", { trip_id: tripId, code });

// The trip's code for people on the trip (and staff), e.g. for invite links.
export const getTripAccessCode = (tripId) =>
  authAxios.get("/v1/trip/getTripAccessCode", { params: { trip_id: tripId } });

export const useSofestTrip = (tripId) => {
  const lang = i18n.language;
  const url = tripId ? `/v1/trip/getSofestTrip?trip_id=${tripId}` : null;
  return useSWR(url ? [url, lang] : null, ([url]) => unauthAxiosFetcher(url));
};

export const searchTrips = (
  months,
  destination,
  search,
  trip,
  user_id,
  region,
  project,
  subregion,
  week,
  year,
  broad,
  advancedFilters = {}
) =>
  unauthAxios.get("/v1/trip/searchTrips", {
    params: {
      months: months,
      destination: destination,
      search: search,
      trip: trip,
      user_id: user_id,
      region: region,
      project: project,
      subregion: subregion,
      week: week,
      year: year,
      broad: broad,
      ...advancedFilters,
    },
  });

export const searchTripsFacets = (
  months,
  destination,
  search,
  trip,
  user_id,
  region,
  project,
  subregion,
  week,
  year,
  broad,
  advancedFilters = {}
) =>
  unauthAxios.get("/v1/trip/searchTripsFacets", {
    params: {
      months,
      destination,
      search,
      trip,
      user_id,
      region,
      project,
      subregion,
      week,
      year,
      broad,
      ...advancedFilters,
    },
  });

// Maps section slugs to i18n translation keys for display
export const SECTION_TITLE_KEYS = {
  "trending": "trending_today",
  "time-limited-offers": "time_limited_offers",
  "beginner": "beginner_trips",
  "lead-confirmed-trips": "lead_confirmed_trips",
  "newly-launched": "newly_launched",
  "weekend-getaways": "weekend_getaways",
  "almost-sold-out": "almost_sold_out",
  "upcoming": "upcoming_adventures",
  "world-wonders": "world_wonder_title",
  "sofest-tobyato": "sofest_tobyato_exclusive_drops",
  // world
  "world-last-frontier": "landing.destination.world-wonders.last_frontier_header",
  "world-aurora": "landing.destination.world-wonders.aurora_header",
  "world-festivals": "landing.destination.world-wonders.world_festivals_header",
  "world-ship": "landing.destination.world-wonders.sea_wayfarers_header",
  "world-safari": "landing.destination.world-wonders.safari_header",
  "world-ancient": "landing.destination.world-wonders.ancient_header",
  "world-gatekeep": "landing.destination.world-wonders.gatekept_header",
  "world-all": "upcoming_adventures",
  // land
  "land-weekend": "weekend_hikes",
  "land-beginner-friendly": "beginner_hikes",
  "land-improver-friendly": "moderate_hikes",
  "land-challenging": "challenging_hikes",
  "land-all": "upcoming_adventures",
  // snow
  "snow-china": "snow_china.title",
  "snow-europe": "snow_europe.title",
  "snow-japan": "snow_japow.title",
  "snow-all": "upcoming_adventures",
  // sea
  "sea-weekend": "short_sea_escape",
  "sea-dive": "asia_best_dive",
  "sea-surf": "surf_camps",
  "sea-all": "upcoming_adventures",
  // 360
  "360-mongolia": "360_page.mongolia.title",
  "360-africa": "360_page.africa.title",
  "360-europe": "360_page.europe.title",
  "360-china": "360_page.china.title",
  "360-india": "360_page.india.title",
  "360-indonesia": "360_page.indonesia.title",
  "360-others": "360_page.others.title",
  "360-all": "upcoming_adventures",
  // club
  "club-sofest": "sofest_events",
  "club-all": "events_near_you",
  // social
  "your-crew": "club_your_crew.title",
  // offers
  "offers-welcome-deals": "welcome_deals",
  "offers-last-minute": "last_minute_deals",
};


export const getUserTrips = async (userId) => {
  return await unauthAxios.get(`/v1/trip/getUserTrips`, {
    params: {
      user_id: userId,
    },
  });
};

export const getUserPastTrips = async (userId) => {
  const res = await unauthAxios.get(`/v1/trip/getUserPastTrips`, {
    params: {
      user_id: userId,
    },
  });
  return [res.data.pastTrips, res.data.hasClimbExperience, res.data.hasSkiExperience];
};

export const createTrip = (projectId, startDate, userId, opts = {}) => {
  return authAxios.post("/v1/trip/createCoordinatedTripAdmin", {
    project_id: projectId,
    start_date: startDate,
    age_restriction: "18-100",
    status: opts.status ?? "waitlist",
    trip_type: "community",
    coordinatorId: userId,
    // soclub_weekly slot booking: time slot + activity-type lock
    start_time: opts.startTime ?? null,
    locked_activity_type_id: opts.lockedActivityTypeId ?? null,
  });
};

export const cancelCoordinatedTripAdmin = (trip_id, project_id) => {
  return authAxios.post("/v1/trip/cancelCoordinatedTripAdmin", {
    trip_id: trip_id,
    project_id: project_id,
  });
};

export const getUsersGoingForTrip = (tripId) => unauthAxios.get(`/v1/trip/${tripId}/users`);

export const giveFeedbackCredits = (tripId) =>
  authAxios.get(`/v1/trip/giveFeedbackCredits?trip_id=${tripId}`);

export const useUsersGoingForTrip = (tripId) =>
  useSWR(`/v1/trip/${tripId}/users`, unauthAxiosFetcher);

export const getUserTrip = (user_id, trip_id) => {
  return unauthAxios.get(`/v1/trip/getUserTrip`, {
    params: {
      user_id: user_id,
      trip_id: trip_id,
    },
  });
};

export const isUserALocalPartnerOrLeader = (user_id, trip_id) =>
  unauthAxios.get(`/v1/trip/isUserALocalPartnerOrLeader`, {
    params: {
      user_id: user_id,
      trip_id: trip_id,
    },
  });

// --- Trip Forms & itinerary ----

// data must contain trip_id
export const upsertUserTrip = (data) => authAxios.post("/v1/trip/upsertUserTrip", data);

export const updateTripGuestInfo = (data) => {
  return authAxios.post("/v1/trip/updateTripGuestInfo", data);
};

// query can be { showUsers: boolean, isCheckout: boolean }
export const getTripGroupForms = (tripId, query) =>
  unauthAxios.get(`/v1/trip/${tripId}/groupForms?${objectToQueryParams(query)}`);

export const getTripInfoForm = (tripId) => authAxios.get(`/v1/trip/${tripId}/infoForm`);

// tripForms is an array of tripForm to be updated in the format
// interface tripForm {
// formId,
// selectedGroupId,
// selectedGroupName,
// selectedGroupDescription,
// }
export const updateTripGroupForms = (tripForms) =>
  authAxios.put(`/v1/trip/groupForms`, { tripForms });

export const updateTripInfoFormAnswers = (infoFormId, data) =>
  authAxios.post(`/v1/trip/infoFormAnswers`, data);

export const getTripIndemnityForm = (tripId) => {
  return authAxios.get(`/v1/trip/${tripId}/indemnityForm`);
};

export const isUserOnTrip = (tripId) => {
  return authAxios.get(`/v1/trip/isUserOnTrip?trip_id=${tripId}`);
};

export const getTripGroups = (tripId) => {
  return authAxios.get(`/v1/trip/getTripGroups?trip_id=${tripId}`);
};

export const joinTripGroup = (tripId, groupKey) => {
  return authAxios.post("/v1/trip/joinGroup", {
    trip_id: tripId,
    group_key: groupKey,
    source: "web",
  });
};

export const getUserWrittenReviews = (tripId) => {
  return authAxios.get(`/v1/trip/getUserWrittenReviews?trip_id=${tripId}`);
};

// query can be { showUsers: boolean, isCheckout: boolean }
export const useTripGroupForms = (tripId, query) =>
  useSWR(`/v1/trip/${tripId}/groupForms?${objectToQueryParams(query)}`, unauthAxiosFetcher);

export const verifyTripPayment = (data) => {
  return authAxios.post("/v1/payment/paymentConfirmation", data);
};

export const upsertTripReview = (review_data) => {
  return authAxios.post("/v1/trip/upsertTripReview", review_data);
};

export const cancelTrip = (cancel_data) => {
  return authAxios.post("/v1/trip/cancelTrip", cancel_data);
};

export const getProjectMemories = async (project_id) => {
  return await unauthAxios.get(`/v1/project/getProjectMemories?project_id=${project_id}`);
};

export const useSpecificInterestCategories = () => {
  return useSWR(`/v1/trip/getSpecificInterestCategories`, unauthAxiosFetcher);
};

export const addTripMediaEntries = async (tripId, uploadedMediaArray) => {
  const response = await authAxios.put("/v1/mobileapp/addMemoryEntries", {
    tripId,
    mediaArray: uploadedMediaArray,
  });

  return response;
};

export const addTripMediaAdmin = async (projectId, uploadedMediaArray) => {
  const response = await authAxios.put("/v1/admin/addTripMedia", {
    projectId,
    mediaArray: uploadedMediaArray,
  });

  return response;
};

export const incrementWebsiteBannerClickCount = async (bannerId) => {
  unauthAxios.post("/v1/trip/incrementWebsiteBannerClickCount", {
    bannerId,
  });
};

export const getBookingRefundEligibility = async (tripId, mainBookerCancelled = false) => {
  return await authAxios.get(
    `/v1/unifiedHandleBooking/getBookingRefundEligibility?bookingId=${tripId}&tripOrActivity=trip&mainBookerCancelled=${mainBookerCancelled}`
  );
};

export const useTripsByRegion = (region) =>
  useSWR(region ? `/v1/trip/getTripsByRegion?region=${region}` : null, unauthAxiosFetcher);

export const useTripsBySubregion = (subregion) =>
  useSWR(subregion ? `/v1/trip/getTripsBySubregion?subregion=${subregion}` : null, unauthAxiosFetcher);

export const useRegionTripCounts = () =>
  useSWR("/v1/trip/getRegionTripCounts", unauthAxiosFetcher);

export const useTripsByMonth = (month, year) =>
  useSWR(
    month ? `/v1/trip/getTripsByMonth?months=${month}${year ? `&year=${year}` : ""}` : null,
    unauthAxiosFetcher
  );

export const useTripsByMonths = (months, year) =>
  useSWR(
    months ? `/v1/trip/getTripsByMonth?months=${months}${year ? `&year=${year}` : ""}` : null,
    unauthAxiosFetcher
  );
